/**
 * D19's "Update on air?": the on-air part's definition was edited while it
 * plays. Its instance changes only for the **live-editable** fields (the
 * engine's `LIVE_EDITABLE_PART_FIELDS`, the one list), and only after an
 * operator with `playout.take` confirms — `syncToInstance`. This finds which
 * of those fields differ between the definition row and the running instance.
 *
 * Other edits to the on-air part do not affect air and are not offered; the
 * next part's instance is re-copied by the production itself (§5.2).
 */
import { LIVE_EDITABLE_PART_FIELDS } from '@norna/playout-engine/sync.js';
import { expandFieldPaths, partFieldsSchema, type PartFields } from '@norna/domain/fields.js';
import type { Row } from '@norna/domain/row.js';
import type { RundownSource } from './viewModel.js';

export type LiveEdit = {
  readonly partInstanceId: string;
  /** The live-editable fields whose definition differs from the instance. */
  readonly fields: readonly string[];
  /**
   * This occurrence: the instance and the definition's values of those fields. A dismissed banner stays away while
   * it is unchanged and comes back when it changes — another edit, or another part on air.
   */
  readonly occurrence: string;
};

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * The pending live edit of the on-air part, or null. `definition` is its
 * definition row as the view holds it (null: deleted, or an adlib part — then
 * there is nothing to sync from).
 */
export function pendingLiveEdit(instanceId: string, instancePart: PartFields, definition: Row | null): LiveEdit | null {
  if (definition === null || definition.f['deleted'] === true) return null;
  const parsed = partFieldsSchema.safeParse(expandFieldPaths(definition.f));
  if (!parsed.success) return null;
  const fields = LIVE_EDITABLE_PART_FIELDS.filter((field) => !same(parsed.data[field], instancePart[field]));
  if (fields.length === 0) return null;
  const values = Object.fromEntries(fields.map((field) => [field, parsed.data[field] ?? null]));
  return { partInstanceId: instanceId, fields, occurrence: `${instanceId}:${JSON.stringify(values)}` };
}

/** A part's definition row in the rundowns as held, or null (not there: deleted for good, or never a row). */
export function definitionIn(rundowns: readonly RundownSource[], partId: string): Row | null {
  for (const r of rundowns) {
    const row = r.tree.rows.get(partId);
    if (row !== undefined) return row;
  }
  return null;
}
